import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import {
  PresetResponse,
  CreatePresetData,
  UpdatePresetData,
  PRESET_AVAILABLE_TIMES_CURFEW_OVERLAP_CODE,
  presetsApi,
} from '../../lib/presets';
import { GroupResponse } from '../../lib/groups';
import { areasApi, AREA_PRESET_TYPES, getAreaPresetTypeLabel, type AreaPresetType } from '../../lib/areas';
import { sitesApi } from '../../lib/sites';
import {
  apiWindowsToAvailabilitySlots,
  availabilitySlotsOverlapCurfewFlags,
  availabilitySlotsToApiPayload,
  type AvailabilitySlot,
} from '../../lib/weekly-availability';
import { WeeklyAvailabilityCalendar } from '@/components/scheduling/WeeklyAvailabilityCalendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PresetFormModalProps {
  preset: PresetResponse | null;
  areaId: string;
  /** Site id for loading site curfew (weekly calendar background). */
  siteId?: string;
  groups: GroupResponse[];
  onSubmit: (data: CreatePresetData | UpdatePresetData) => Promise<PresetResponse>;
  onClose: () => void;
  isSubmitting: boolean;
}

function axiosErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error ?? e.message ?? 'Request failed';
}

function curfewOverlapDescription(flags: {
  overlapsSite: boolean;
  overlapsArea: boolean;
}): string {
  const parts: string[] = [];
  if (flags.overlapsSite) parts.push('site lighting curfew');
  if (flags.overlapsArea) parts.push('area lighting curfew');
  const which =
    parts.length > 0 ? parts.join(' and ') : 'site or area lighting curfew';
  return `Some weekly available times fall within ${which}. Those windows overlap curfew; lights may still be blocked during curfew. Save anyway?`;
}

type CurfewConfirmState =
  | null
  | { mode: 'before-save' }
  | { mode: 'retry-put'; presetId: string };

export default function PresetFormModal({
  preset,
  areaId,
  siteId,
  groups,
  onSubmit,
  onClose,
  isSubmitting,
}: PresetFormModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isEditing = !!preset;
  const isSiteAdmin = user?.role === 'site_admin';
  const canManageSchedule =
    user?.role === 'super_user' || user?.role === 'site_admin';

  const { data: area } = useQuery({
    queryKey: ['area', areaId],
    queryFn: () => areasApi.getById(areaId),
    enabled: !!areaId,
  });

  const { data: site } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.getById(siteId!),
    enabled: !!siteId && canManageSchedule,
  });

  const { data: availabilityRows } = useQuery({
    queryKey: ['preset-available-times', preset?.id],
    queryFn: () => presetsApi.getAvailableTimes(preset!.id),
    enabled: !!preset?.id && canManageSchedule && preset.type === 'light',
  });

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    type: 'user' as AreaPresetType,
    userGroupId: '',
    groupIds: [] as string[],
  });

  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [curfewConfirm, setCurfewConfirm] = useState<CurfewConfirmState>(null);
  const [curfewOverlapDetail, setCurfewOverlapDetail] = useState<{
    overlapsSite: boolean;
    overlapsArea: boolean;
  } | null>(null);

  useEffect(() => {
    if (preset) {
      const addressString =
        preset.address !== null && preset.address !== undefined
          ? String(preset.address)
          : '';
      setFormData({
        name: preset.name,
        address: addressString,
        type: preset.type || 'user',
        userGroupId: preset.userGroupId || '',
        groupIds: preset.groups?.map((g) => g.groupId) || [],
      });
    } else {
      const defaultType = area?.type || 'user';
      setFormData({
        name: '',
        address: '',
        type: defaultType,
        userGroupId: '',
        groupIds: [],
      });
    }
  }, [preset, area]);

  useEffect(() => {
    if (!preset?.id || preset.type !== 'light') {
      setAvailabilitySlots([]);
      return;
    }
    if (availabilityRows !== undefined) {
      setAvailabilitySlots(apiWindowsToAvailabilitySlots(availabilityRows));
    }
  }, [preset?.id, preset?.type, availabilityRows]);

  const handleGroupToggle = (groupId: string) => {
    setFormData((prev) => {
      const isSelected = prev.groupIds.includes(groupId);
      if (isSelected) {
        return {
          ...prev,
          groupIds: prev.groupIds.filter((id) => id !== groupId),
        };
      }
      return {
        ...prev,
        groupIds: [...prev.groupIds, groupId],
      };
    });
  };

  const buildSubmitPayload = (): CreatePresetData | UpdatePresetData => {
    if (isSiteAdmin && isEditing) {
      return {
        userGroupId: formData.userGroupId === '' ? null : formData.userGroupId || undefined,
        groupIds: formData.groupIds.length > 0 ? formData.groupIds : undefined,
      };
    }
    let addressValue: number | undefined = undefined;
    if (formData.address !== '') {
      const parsed = parseInt(formData.address, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 255) {
        addressValue = parsed;
      }
    }
    const payload: CreatePresetData | UpdatePresetData = {
      name: formData.name,
      address: addressValue,
      type: formData.type,
      userGroupId: formData.userGroupId === '' ? null : formData.userGroupId || undefined,
      groupIds: formData.groupIds.length > 0 ? formData.groupIds : undefined,
    };
    if (!isEditing) {
      (payload as CreatePresetData).areaId = areaId;
    }
    return payload;
  };

  const retryPutAfterCurfewConfirm = async (presetId: string) => {
    setSaveError(null);
    setIsSavingAll(true);
    try {
      await presetsApi.putAvailableTimes(
        presetId,
        availabilitySlotsToApiPayload(availabilitySlots),
        { confirmCurfewOverlap: true },
      );
      await queryClient.invalidateQueries({ queryKey: ['preset-available-times', presetId] });
      onClose();
    } catch (err) {
      setSaveError(axiosErrorMessage(err));
    } finally {
      setIsSavingAll(false);
    }
  };

  const runSaveFlow = async (confirmCurfewOverlap: boolean) => {
    setSaveError(null);
    setIsSavingAll(true);
    try {
      const payload = buildSubmitPayload();
      const saved = await onSubmit(payload);

      const shouldPersistSchedule =
        canManageSchedule &&
        area?.type === 'light' &&
        saved.type === 'light';

      if (shouldPersistSchedule) {
        try {
          await presetsApi.putAvailableTimes(
            saved.id,
            availabilitySlotsToApiPayload(availabilitySlots),
            { confirmCurfewOverlap },
          );
        } catch (putErr) {
          const ax = putErr as {
            response?: {
              status?: number;
              data?: {
                code?: string;
                overlapsSite?: boolean;
                overlapsArea?: boolean;
              };
            };
          };
          if (
            ax.response?.status === 409 &&
            ax.response.data?.code === PRESET_AVAILABLE_TIMES_CURFEW_OVERLAP_CODE &&
            !confirmCurfewOverlap
          ) {
            const d = ax.response.data;
            setCurfewOverlapDetail({
              overlapsSite: !!d.overlapsSite,
              overlapsArea: !!d.overlapsArea,
            });
            setCurfewConfirm({ mode: 'retry-put', presetId: saved.id });
            return;
          }
          throw putErr;
        }
        await queryClient.invalidateQueries({ queryKey: ['preset-available-times', saved.id] });
      }

      onClose();
    } catch (err) {
      setSaveError(axiosErrorMessage(err));
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const willPersistAvailability =
      canManageSchedule &&
      area?.type === 'light' &&
      (isEditing ? preset?.type === 'light' : formData.type === 'light');

    if (willPersistAvailability) {
      const overlap = availabilitySlotsOverlapCurfewFlags(
        availabilitySlots,
        { start: site?.curfewStart, end: site?.curfewEnd },
        { start: area?.curfewStart, end: area?.curfewEnd },
      );
      if (overlap.overlapsSite || overlap.overlapsArea) {
        setCurfewOverlapDetail(overlap);
        setCurfewConfirm({ mode: 'before-save' });
        return;
      }
    }

    void runSaveFlow(false);
  };

  const busy = isSavingAll || isSubmitting;

  return (
    <>
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[920px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Preset' : 'Create Preset'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Name *
            </Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={isSiteAdmin && isEditing}
              readOnly={isSiteAdmin && isEditing}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">
              Preset Address
            </Label>
            <Input
              id="address"
              type="number"
              min="0"
              max="255"
              value={formData.address}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 255)) {
                  setFormData({ ...formData, address: value });
                }
              }}
              placeholder="0-255"
              disabled={isSiteAdmin && isEditing}
              readOnly={isSiteAdmin && isEditing}
            />
            <p className="text-xs text-muted-foreground">Optional: Preset address (0-255) for PDEG</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Type
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  type: value as AreaPresetType,
                  userGroupId: value === 'user' ? formData.userGroupId : '',
                });
              }}
              disabled={isSiteAdmin && isEditing}
            >
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AREA_PRESET_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getAreaPresetTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Preset type (must match area type)</p>
          </div>

          {formData.type === 'user' && (
            <div className="space-y-2">
              <Label htmlFor="userGroupId">
                User Group
              </Label>
              <Select
                value={formData.userGroupId || "__none__"}
                onValueChange={(value) =>
                  setFormData({ ...formData, userGroupId: value === "__none__" ? "" : value })
                }
              >
                <SelectTrigger id="userGroupId">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optional: Associate with a group (only for &apos;user&apos; type presets)
              </p>
            </div>
          )}

          {formData.type === 'light' && (
            <div className="space-y-2">
              <Label>User Groups</Label>
              <Card className="max-h-48 overflow-y-auto p-2">
                {groups && groups.length > 0 ? (
                  <div className="space-y-2">
                    {groups.map((group) => {
                      const isSelected = formData.groupIds.includes(group.id);

                      return (
                        <div key={group.id} className="py-2 border-b last:border-b-0">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleGroupToggle(group.id)}
                            />
                            <div>
                              <p className="text-sm font-medium">{group.name}</p>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">No groups available</p>
                )}
              </Card>
            </div>
          )}

          {canManageSchedule && area?.type === 'light' && formData.type === 'light' && (
            <div className="space-y-2 border-t border-border pt-4">
              <Label className="text-base">Weekly available times</Label>
              <p className="text-xs text-muted-foreground">
                Add availability times for a preset. The system will use these to enforce availability windows. Note that availability times must be defined for a light area preset to be available for use. If in doubt, set availability to all non-curfew times with the button below.
              </p>
              <WeeklyAvailabilityCalendar
                siteCurfew={{ start: site?.curfewStart, end: site?.curfewEnd }}
                areaCurfew={{ start: area?.curfewStart, end: area?.curfewEnd }}
                value={availabilitySlots}
                onChange={setAvailabilitySlots}
                disabled={busy}
              />
            </div>
          )}

          {saveError && (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              <Save className="h-4 w-4 mr-2" />
              {busy ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Preset'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog
      open={curfewConfirm !== null}
      onOpenChange={(open) => {
        if (!open) {
          setCurfewConfirm(null);
          setCurfewOverlapDetail(null);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Available times overlap curfew</AlertDialogTitle>
          <AlertDialogDescription>
            {curfewOverlapDetail
              ? curfewOverlapDescription(curfewOverlapDetail)
              : 'Some available times overlap site or area curfew. Save anyway?'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const pending = curfewConfirm;
              setCurfewConfirm(null);
              setCurfewOverlapDetail(null);
              if (pending?.mode === 'before-save') {
                void runSaveFlow(true);
              } else if (pending?.mode === 'retry-put') {
                void retryPutAfterCurfewConfirm(pending.presetId);
              }
            }}
          >
            Save anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
